import { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronDown, Pencil, Copy, Check, Trash2 } from 'lucide-react';
import { VariablePill, parseContentWithVariables } from './VariablePill';
import { AIRegenerateButton } from './AIRegenerateButton';
import { VariableInsertPanel } from './VariableInsertPanel';
import SimpleCustomSelect from '../shared/SimpleCustomSelect';
import type { TemplateVariable } from '../../hooks/useVariableInsertion';

type CategoryType = 'timing' | 'budget' | 'authority' | 'need' | 'competition' | 'other';

const CATEGORY_OPTIONS: { value: CategoryType; label: string }[] = [
  { value: 'timing', label: 'Timing' },
  { value: 'budget', label: 'Budget' },
  { value: 'authority', label: 'Authority' },
  { value: 'need', label: 'Need' },
  { value: 'competition', label: 'Competition' },
  { value: 'other', label: 'Other' },
];

interface ObjectionCardProps {
  title: string;
  response: string;
  category?: CategoryType;
  onUpdateTitle: (title: string) => void;
  onUpdateResponse: (response: string) => void;
  onUpdateCategory?: (category: CategoryType) => void;
  onDelete: () => void;
  onGenerateAI?: (tone: string, length: string, currentContent: string) => Promise<string>;
  onDirtyChange?: (isDirty: boolean) => void;
  defaultExpanded?: boolean;
  variables?: TemplateVariable[];
}

const categoryColors = {
  timing: 'bg-blue-100 text-blue-700',
  budget: 'bg-green-100 text-green-700',
  authority: 'bg-purple-100 text-purple-700',
  need: 'bg-orange-100 text-orange-700',
  competition: 'bg-red-100 text-red-700',
  other: 'bg-gray-100 text-gray-700',
};

const categoryLabels = {
  timing: 'Timing',
  budget: 'Budget',
  authority: 'Authority',
  need: 'Need',
  competition: 'Competition',
  other: 'Other',
};

export function ObjectionCard({
  title,
  response,
  category = 'other',
  onUpdateTitle,
  onUpdateResponse,
  onUpdateCategory,
  onDelete,
  onGenerateAI,
  onDirtyChange,
  defaultExpanded = false,
  variables,
}: ObjectionCardProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(title);
  const [editResponse, setEditResponse] = useState(response);
  const [editCategory, setEditCategory] = useState<CategoryType>(category);
  const [copied, setCopied] = useState(false);
  const [showVariables, setShowVariables] = useState(false);
  const [insertedVar, setInsertedVar] = useState<string | null>(null);

  const titleInputRef = useRef<HTMLInputElement>(null);
  const responseTextareaRef = useRef<HTMLTextAreaElement>(null);
  const cursorPosRef = useRef<number>(0);
  const activeFieldRef = useRef<'title' | 'response'>('response');

  const trackCursor = useCallback((field: 'title' | 'response') => {
    activeFieldRef.current = field;
    const el = field === 'title' ? titleInputRef.current : responseTextareaRef.current;
    if (el) {
      cursorPosRef.current = el.selectionStart ?? 0;
    }
  }, []);

  const handleInsertVariable = useCallback((varName: string) => {
    const field = activeFieldRef.current;
    const el = field === 'title' ? titleInputRef.current : responseTextareaRef.current;
    const currentContent = field === 'title' ? editTitle : editResponse;
    const pos = cursorPosRef.current;
    const before = currentContent.slice(0, pos);
    const after = currentContent.slice(pos);
    const newContent = before + varName + after;

    if (field === 'title') {
      setEditTitle(newContent);
    } else {
      setEditResponse(newContent);
    }

    setInsertedVar(varName);
    setTimeout(() => setInsertedVar(null), 600);

    requestAnimationFrame(() => {
      if (el) {
        const newPos = pos + varName.length;
        el.focus();
        el.setSelectionRange(newPos, newPos);
        cursorPosRef.current = newPos;
      }
    });
  }, [editTitle, editResponse]);

  useEffect(() => {
    if (isEditing) {
      onDirtyChange?.(editTitle !== title || editResponse !== response || editCategory !== category);
    }
  }, [isEditing, editTitle, editResponse, editCategory, title, response, category]);

  const handleCopy = () => {
    navigator.clipboard.writeText(response);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSave = () => {
    onUpdateTitle(editTitle);
    onUpdateResponse(editResponse);
    if (onUpdateCategory) onUpdateCategory(editCategory);
    setIsEditing(false);
    setShowVariables(false);
    onDirtyChange?.(false);
  };

  const handleCancel = () => {
    setEditTitle(title);
    setEditResponse(response);
    setEditCategory(category);
    setIsEditing(false);
    setShowVariables(false);
    onDirtyChange?.(false);
  };

  const cleanObjectionResponse = (raw: string): string => {
    let text = raw.trim();

    const responseMatch = text.match(/RESPONSE:\s*([\s\S]*?)(?:\n===|$)/i);
    if (responseMatch) {
      text = responseMatch[1].trim();
    } else {
      text = text
        .split('\n')
        .filter(line => {
          const trimmed = line.trim();
          return !trimmed.match(/^CATEGORY:\s/i) &&
            !trimmed.match(/^OBJECTION:\s/i) &&
            trimmed !== '===';
        })
        .join('\n')
        .trim();
    }

    text = text.replace(/^Response:\s*/i, '');
    text = text.replace(/^["']|["']$/g, '');

    return text.trim();
  };

  const handleAcceptGenerated = (generatedContent: string) => {
    const cleaned = cleanObjectionResponse(generatedContent);
    setEditResponse(cleaned);
    onUpdateResponse(cleaned);
  };

  const renderPreview = () => {
    if (!response) {
      return (
        <p className="text-gray-400 text-sm italic">
          No response configured yet
        </p>
      );
    }

    const lines = response.split('\n').slice(0, 2);
    const previewText = lines.join('\n');
    const hasMore = response.split('\n').length > 2 || previewText.length > 150;

    return (
      <div className="text-sm text-gray-700 leading-relaxed">
        {parseContentWithVariables(previewText.substring(0, 150)).map((part, idx) => {
          if (part.type === 'variable') {
            return (
              <VariablePill
                key={idx}
                variable={part.content}
                type={part.variableType}
              />
            );
          }
          return <span key={idx}>{part.content}</span>;
        })}
        {hasMore && (
          <span className="text-gray-400 ml-1">...</span>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200">
      <div
        className="p-5 cursor-pointer"
        onClick={() => !isEditing && setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1">
            <ChevronDown
              className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
                isExpanded ? 'rotate-180' : ''
              }`}
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-base font-bold text-gray-900 font-display">
                  {title}
                </h3>
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${categoryColors[category]}`}>
                  {categoryLabels[category]}
                </span>
                {!response && (
                  <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-medium rounded-full">
                    Draft
                  </span>
                )}
              </div>
              {!isExpanded && !isEditing && (
                <div className="mt-2">{renderPreview()}</div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 ml-4" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => {
                setEditTitle(title);
                setEditResponse(response);
                setEditCategory(category);
                setIsEditing(true);
                setIsExpanded(true);
              }}
              className="p-2 text-gray-400 hover:text-[#1875FF] hover:bg-[#1875FF]/10 rounded-lg transition-colors"
              title="Edit"
            >
              <Pencil className="w-5 h-5" />
            </button>
            {onGenerateAI && (
              <AIRegenerateButton
                onRegenerate={onGenerateAI}
                onAccept={handleAcceptGenerated}
                currentContent={response}
              />
            )}
            <button
              onClick={handleCopy}
              disabled={!response}
              className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-30"
              title="Copy"
            >
              {copied ? (
                <Check className="w-5 h-5 text-green-600" />
              ) : (
                <Copy className="w-5 h-5" />
              )}
            </button>
            <button
              onClick={() => {
                if (confirm('Are you sure you want to delete this objection?')) {
                  onDelete();
                }
              }}
              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title="Delete"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="px-5 pb-5 border-t border-gray-100">
          {isEditing ? (
            <div className="space-y-3 pt-4">
              <div className="flex items-center gap-3">
                <input
                  ref={variables ? titleInputRef : undefined}
                  type="text"
                  value={editTitle}
                  onChange={(e) => {
                    setEditTitle(e.target.value);
                    if (variables) trackCursor('title');
                  }}
                  onSelect={variables ? () => trackCursor('title') : undefined}
                  onClick={variables ? () => trackCursor('title') : undefined}
                  onKeyUp={variables ? () => trackCursor('title') : undefined}
                  placeholder="e.g., I'm too busy right now"
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] text-sm font-medium transition-all"
                />
                <SimpleCustomSelect
                  value={editCategory}
                  onChange={(val) => setEditCategory(val as CategoryType)}
                  options={CATEGORY_OPTIONS}
                  placeholder="Category"
                  className="w-36"
                />
              </div>
              <div className="relative">
                <textarea
                  ref={variables ? responseTextareaRef : undefined}
                  value={editResponse}
                  onChange={(e) => {
                    setEditResponse(e.target.value);
                    if (variables) trackCursor('response');
                  }}
                  onSelect={variables ? () => trackCursor('response') : undefined}
                  onClick={variables ? () => trackCursor('response') : undefined}
                  onKeyUp={variables ? () => trackCursor('response') : undefined}
                  placeholder="How you'll respond to this objection..."
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] text-sm resize-none"
                />
              </div>

              {variables && (
                <VariableInsertPanel
                  variables={variables}
                  showVariables={showVariables}
                  onToggle={() => setShowVariables(!showVariables)}
                  onInsert={handleInsertVariable}
                  insertedVar={insertedVar}
                />
              )}

              <div className="flex justify-end gap-2">
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-4 py-2 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] text-white rounded-lg hover:shadow-lg hover:scale-105 transition-all text-sm font-medium"
                >
                  Save Changes
                </button>
              </div>
            </div>
          ) : (
            <div className="pt-4">
              <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                {parseContentWithVariables(response).map((part, idx) => {
                  if (part.type === 'variable') {
                    return (
                      <VariablePill
                        key={idx}
                        variable={part.content}
                        type={part.variableType}
                      />
                    );
                  }
                  return <span key={idx}>{part.content}</span>;
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
